// Current product boundary: matchmaking provides collaboration tools only.
export const collaborationOnly = true;
export const beijingDateTime = (now = new Date()) =>
  new Date(now.getTime() + 8 * 3600000).toISOString().slice(0, 16);
export const beijingTimestamp = (value: string) => {
  if (!value) return '';
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?$/.test(value)) {
    throw new Error('请选择有效的北京时间');
  }
  const local = value.length === 16 ? `${value}:00` : value;
  const instant = new Date(`${local}+08:00`);
  if (
    !Number.isFinite(instant.getTime()) ||
    new Date(instant.getTime() + 8 * 3600000).toISOString().slice(0, 19) !== local
  ) {
    throw new Error('请选择有效的北京时间');
  }
  return instant.toISOString().replace('.000Z', 'Z');
};
// All quote and inquiry calendar dates follow the platform's Beijing business day.
export const beijingToday = (now = new Date()) =>
  new Date(now.getTime() + 8 * 60 * 60 * 1000).toISOString().slice(0, 10);
export const money = (minor: number, compact = false) =>
  new Intl.NumberFormat('zh-CN', {
    style: 'currency',
    currency: 'CNY',
    maximumFractionDigits: compact || minor % 100 === 0 ? 0 : 2,
  }).format(minor / 100);
// A mismatched receipt must retain its actual currency. Unknown currencies
// are shown in original smallest units, without guessing a conversion scale.
export const paymentFactAmount = (minor: number, currency?: string) =>
  currency === 'CNY'
    ? money(minor)
    : `${currency || '未知币种'} ${new Intl.NumberFormat('zh-CN', { maximumFractionDigits: 0 }).format(minor)}（最小单位）`;
export const date = (value?: string) =>
  value
    ? new Intl.DateTimeFormat('zh-CN', { month: 'long', day: 'numeric' }).format(new Date(value))
    : '待确认';
export const datetime = (value?: string) =>
  value
    ? new Intl.DateTimeFormat('zh-CN', {
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(value))
    : '待安排';
export const fullDatetime = (value?: string) =>
  value
    ? new Intl.DateTimeFormat('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(value))
    : '待安排';
export const tags = (value?: string) => value?.split(',').filter(Boolean) ?? [];
export function reviewScore(review: {
  overall_rating?: number;
  rating: number;
  quality_rating?: number;
  communication_rating?: number;
  timeliness_rating?: number;
  professionalism_rating?: number;
}) {
  const values = [
    review.quality_rating,
    review.communication_rating,
    review.timeliness_rating,
    review.professionalism_rating,
  ];
  const score = values.every((value) => Number.isInteger(value) && value! >= 1 && value! <= 5)
    ? values.reduce<number>((sum, value) => sum + value!, 0) / 4
    : review.overall_rating || review.rating;
  return Number(score || 0).toFixed(1);
}
export const reviewRatingOptions = [
  { value: '', label: '请选择评分' },
  { value: '5', label: '5 星 · 非常满意' },
  { value: '4', label: '4 星 · 满意' },
  { value: '3', label: '3 星 · 一般' },
  { value: '2', label: '2 星 · 不满意' },
  { value: '1', label: '1 星 · 非常不满意' },
];
export function siteURL(site: 'www' | 'work' | 'admin', path = '') {
  const configured = {
    www: import.meta.env.VITE_WWW_ORIGIN,
    work: import.meta.env.VITE_WORK_ORIGIN,
    admin: import.meta.env.VITE_ADMIN_ORIGIN,
  }[site];
  if (configured) return String(configured).replace(/\/$/, '') + path;
  const local =
    typeof window === 'undefined' || ['localhost', '127.0.0.1'].includes(window.location.hostname);
  if (local) return `http://localhost:${{ www: 5173, work: 5174, admin: 5175 }[site]}${path}`;
  const fallback = {
    www: 'https://yingbaba.com',
    work: 'https://work.yingbaba.com',
    admin: 'https://admin.yingbaba.com',
  }[site];
  return fallback + path;
}
export const categories = [
  '品牌宣传片',
  '产品广告',
  '短视频',
  '纪录片',
  '动画制作',
  '活动拍摄',
  '后期制作',
];
export const caseCategories = [...categories, '商业摄影', '视觉创意', '幕后制作'];
export const resourceCategories = [
  '场景',
  '模特演员',
  '宠物',
  '文案策划',
  '摄影师',
  '导演',
  '编导',
  '美术指导',
  '配音',
  '剪辑师',
  '器材',
];
export const cityOptions = ['上海', '北京', '杭州', '深圳', '广州', '成都', '全国'];
export const projectTabs = [
  'overview',
  'team',
  'tasks',
  'activity',
  'comments',
  'meetings',
  'files',
  'acceptance',
  'deliveries',
  'reviews',
  'changes',
  'order',
  'exceptions',
] as const;
export type ProjectTab = (typeof projectTabs)[number];

const projectTabAliases: Record<string, ProjectTab> = {
  collaboration: 'team',
  delivery: 'deliveries',
  business: 'order',
};

export function normalizeProjectTab(value?: string | null): ProjectTab {
  if (value && projectTabs.includes(value as ProjectTab)) return value as ProjectTab;
  return (value && projectTabAliases[value]) || 'overview';
}

export const safeReturn = (value: string | null) => {
  if (!value?.startsWith('/') || value.startsWith('//') || /[\\\u0000-\u001f]/.test(value))
    return '/';
  return value;
};

export {
  parseMemberMoney,
  memberBenefitText,
  memberStatus,
  memberCommandKey,
  clearMemberCommand,
} from './membership';
export { supportCaseStatus, supportOrderStatus, supportOrderURL } from './support';
export { digitalPurchasePath, digitalProjectAssetPath } from './digital-links';
export { digitalPreviews, digitalPreviewLabel } from './digital-preview';
